import { Flame, Sparkles, Zap } from "lucide-react";

const STORY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/91b039dc-51e4-4e53-aaae-68155d45e4bb/images/7facf976f9eb478191648aa52cea98a77e16f5b2845bf906437e930acc485eee.png";

const timeline = [
  {
    year: "01",
    title: "DE FAMILIEBORREL",
    desc: "Zaterdagavond bij Jans ouders thuis. Glazen, gelach, en twee oude vrienden — Jan en Bert — die het opnieuw over 'ooit een eigen ding beginnen' hadden.",
  },
  {
    year: "02",
    title: "HET IDEE",
    desc: "Geen plannen op papier, geen pitchdeck. Gewoon de vraag: wat ontbreekt er nog op de markt? Iets dat het samenkomen vierde. Iets om aan te tikken.",
  },
  {
    year: "03",
    title: "HET BRAINSTORMEN",
    desc: "Avonden vol schetsen op bierviltjes, twijfels, tegenideeën en nóg een proefronde. Tot het concept stond: een kleine, krachtige shot met een duidelijk ritueel.",
  },
  {
    year: "04",
    title: "TIK HEM AAN!",
    desc: "Naam, smaak en filosofie kwamen samen. Eén kleine fles. Eén grote tik. Geboren uit een familieborrel — gemaakt voor elke avond die er nog volgt.",
  },
];

const Verhaal = () => (
  <div data-testid="verhaal-page" className="relative">
    <section className="relative py-24 sm:py-32 border-b border-[var(--tha-border)]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
            HET VERHAAL
          </span>
        </div>
        <h1 className="font-display text-6xl sm:text-8xl md:text-9xl mt-6 leading-[0.85] rise">
          BEGONNEN
          <br />
          BIJ EEN <span className="neon-red-text">BORREL.</span>
          <br />
          UITGEWERKT
          <br />
          MET LEF.
        </h1>
      </div>
    </section>

    <section className="relative py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <div className="relative aspect-[4/5] border border-[var(--tha-border)] overflow-hidden">
            <img
              src={STORY_IMG}
              alt="Familieavond"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5">
              <div className="text-[10px] font-mono-x tracking-widest text-white/80">
                #001 — DE OORSPRONG
              </div>
              <div className="font-display text-2xl text-white mt-1">
                EEN ZATERDAGAVOND DIE BLEEF HANGEN.
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <p className="text-lg text-[var(--tha-muted)] leading-relaxed">
            Het begon op een zaterdagavond. De familieborrel bij Jans ouders thuis — het
            soort avond met te veel verhalen, te veel meningen en glazen die je nooit
            helemaal kunt bijhouden.
          </p>
          <p className="mt-5 text-[var(--tha-muted)] leading-relaxed">
            Tussen het gelach door raakten <span className="text-white font-medium">Jan
            en Bert</span> in gesprek. Twee vrienden die het al jaren over hetzelfde
            hadden: ooit een eigen bedrijf beginnen. Niet voor de vorm, maar omdat ze
            écht iets wilden bouwen. Iets dat hen zou herinneren aan precies dit moment:
            mensen aan tafel, drank in het midden, en de tijd die even stilstaat.
          </p>
          <p className="mt-5 text-[var(--tha-muted)] leading-relaxed">
            De brainstorm begon die avond en hield daarna niet meer op. Avond na avond
            uitwerken, twijfelen, proeven, opnieuw beginnen — tot het idee stond: een
            klein flesje met grote impact. Een shot met een eigen ritueel. Iets dat de
            markt nog niet had gezien.
          </p>
          <p className="mt-5 text-[var(--tha-muted)] leading-relaxed">
            Zo werd <span className="text-white font-medium">Tik Hem Aan!</span> geboren —
            geboren bij een borrel, gemaakt voor elke avond die er nog volgt. Glas omhoog,
            één tikje, en weg. Dat is het hele idee. Dat is alles wat we nodig hadden.
          </p>

          <div className="mt-14 space-y-px bg-[var(--tha-border)]">
            {timeline.map((t, i) => (
              <div
                key={i}
                data-testid={`timeline-${i}`}
                className="bg-[#0a0a0a] p-6 sm:p-8 flex items-start gap-6 group hover:bg-[#111] transition-colors"
              >
                <div className="font-display text-5xl text-[var(--tha-red)] leading-none w-20 shrink-0">
                  {t.year}
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl tracking-wide">{t.title}</div>
                  <p className="mt-2 text-sm text-[var(--tha-muted)] leading-relaxed">
                    {t.desc}
                  </p>
                </div>
                <Flame
                  size={18}
                  className="text-[var(--tha-muted)] group-hover:text-[var(--tha-red)] transition-colors shrink-0"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* DE OPRICHTERS */}
    <section
      data-testid="founders-section"
      className="relative py-24 border-t border-[var(--tha-border)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
            DE OPRICHTERS
          </span>
        </div>
        <h2 className="font-display text-5xl sm:text-7xl mt-6 leading-none max-w-4xl">
          TWEE KOPPEN. <span className="neon-red-text">NUL HAREN.</span>
          <br />
          ÉÉN MISSIE.
        </h2>
        <p className="mt-5 text-[var(--tha-muted)] max-w-xl">
          Geen foto's nodig — je herkent ons aan de glimmende koppen aan de bar. Wij zijn
          Jan en Bert. Twee vrienden, één gedeelde haarlijn, en een drankje dat er nooit
          had moeten komen als we ons hadden laten ompraten.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--tha-border)]">
          {[
            {
              id: "jan",
              name: "JAN",
              role: "DE BEDENKER",
              icon: Sparkles,
              quote: "Als het op een bierviltje past, kan het bestaan.",
              traits: [
                { k: "BIJDRAGE", v: "Het idee. Het concept. De koppigheid om door te zetten." },
                { k: "SPECIALITEIT", v: "Zaterdagavond-filosofie tussen het tweede en derde glas." },
                { k: "SHOT-VOLGORDE", v: "Eerst aantikken, dan opdrinken, dan opnieuw inschenken." },
                { k: "LIEVELINGSAVOND", v: "Zaterdag bij familie — daar waar het allemaal begon." },
                { k: "HAARDOSSIER", v: "Vrijwillig kaal. Spaart tijd in de ochtend." },
              ],
            },
            {
              id: "bert",
              name: "BERT",
              role: "DE DOORZETTER",
              icon: Zap,
              quote: "Eerst proeven. Dan praten. Dan tikken.",
              traits: [
                { k: "BIJDRAGE", v: "Recept, smaak, en alle 'nee, dat werkt zo niet' momenten." },
                { k: "SPECIALITEIT", v: "Op het juiste moment 'ja' of 'kom op' zeggen." },
                { k: "SHOT-VOLGORDE", v: "Eerst ruiken, dan rondje uitdelen, en als laatste zelf." },
                { k: "LIEVELINGSAVOND", v: "Vrijdag laat — als de werkweek z'n laatste tik krijgt." },
                { k: "HAARDOSSIER", v: "Ook kaal. Geen toeval, gewoon goede smaak." },
              ],
            },
          ].map((p) => (
            <div
              key={p.id}
              data-testid={`founder-${p.id}`}
              className="relative bg-[#070707] p-8 sm:p-10 overflow-hidden"
            >
              {/* Bald silhouette */}
              <div className="flex items-start justify-between gap-4">
                <BaldSilhouette />
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--tha-border)] text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                  <p.icon size={11} className="text-[var(--tha-red)]" /> {p.role}
                </div>
              </div>

              <div className="mt-8 font-display text-6xl sm:text-7xl text-white leading-none">
                {p.name}
              </div>

              <div className="mt-6 border-l-2 border-[var(--tha-red)] pl-4 italic text-[var(--tha-muted)]">
                "{p.quote}"
              </div>

              <dl className="mt-8 space-y-3">
                {p.traits.map((t) => (
                  <div key={t.k} className="border-t border-[var(--tha-border)] pt-3">
                    <dt className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                      {t.k}
                    </dt>
                    <dd className="mt-1 text-sm text-white">{t.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-3 text-xs font-mono-x tracking-[0.3em] text-[var(--tha-muted)]">
            <span className="h-px w-8 bg-[var(--tha-border)]" />
            ZONDER HAAR, MAAR MET LEF
            <span className="h-px w-8 bg-[var(--tha-border)]" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

const BaldSilhouette = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="56"
    height="56"
    viewBox="0 0 56 56"
    aria-hidden="true"
    className="text-[var(--tha-red)]"
  >
    {/* head */}
    <ellipse cx="28" cy="22" rx="13" ry="14" fill="none" stroke="currentColor" strokeWidth="2" />
    {/* shoulders */}
    <path
      d="M8 52c0-10 9-16 20-16s20 6 20 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* shine on bald head */}
    <ellipse cx="24" cy="14" rx="3" ry="1.5" fill="currentColor" opacity="0.8" />
  </svg>
);

export default Verhaal;
